<template>
	<div class="TagsElement">
		<div
			class="ChipsList"
			ref="chipsListRef"
			:class="{ expanded, hasOverflow }"
		>
			<Chip
				v-for="(chip, idx) in chips"
				:key="chip.value ?? chip.label ?? idx"
				:label="chip.label"
				:count="chip.count"
				@remove="chipRemoveHandler(idx)"
			/>
		</div>
		<div v-if="hasOverflow" class="TagsActions">
			<button
				class="ShowMoreBtn"
				@click="toggleExpand"
			>
				{{ expanded ? 'Show less' : 'Show more' }}
				<i class="ShowMoreImg" :class="expanded ? 'lnr-minus' : 'lnr-plus'"></i>
			</button>
		</div>
	</div>
</template>

<script setup lang="ts">
import { nextTick, onMounted, ref, watch } from 'vue';
import Chip from './Chip.vue';
import type { IChip } from '@app/interfaces/dtos/IChip';
import { isMobile } from '@app/composables/useMobile';

const PADDING_PER_CHIP = 12;
const ESTIMATED_ROW_CAPACITY = 94;

const props = defineProps<{
	chips: IChip[];
}>();

const emit = defineEmits(['elementRemovalRequest']);
const chipsListRef = ref<HTMLElement | null>(null);
const expanded = ref(false);

/**
 * This is just magic, don't ask
 * If REALLY wanna know:
 *  - We need the button to not "just appear" after page load
 *  - We cannot know the height of the container UNTIL the chips are rendered
 *  - We cannot rely on CSS only, because we need to know if the button is needed or not
 *  - So we do a heuristic calculation based on text length to decide if we need the button at first
 *  - Then we do a proper check after rendering to see if the content overflows
 */
const heuristicOverflowCalculation = (): boolean => {
	const totalTextLength = props.chips.reduce((sum, chip) => {
		const labelLength = chip.label?.length || 0;
		return sum + labelLength + PADDING_PER_CHIP;
	}, 0);

	if (totalTextLength > ESTIMATED_ROW_CAPACITY) return true;

	return false;
};

const hasOverflow = ref(heuristicOverflowCalculation());

const checkOverflow = () => {
	if (!chipsListRef.value) return;

	const scrollHeight = chipsListRef.value.scrollHeight;
	hasOverflow.value = scrollHeight > 86;

	if (!hasOverflow.value) return;

	chipsListRef.value.style.setProperty('--expanded-height', `${scrollHeight}px`);
}

onMounted(() => {
	nextTick(() => checkOverflow());
});

watch(() => props.chips, () => {
	nextTick(() => checkOverflow());
});

watch(isMobile, () => {
	nextTick(() => checkOverflow());
});

const chipRemoveHandler = (idx: number) => {
	emit('elementRemovalRequest', idx);
}

const toggleExpand = () => {
	expanded.value = !expanded.value;
}
</script>

<style scoped lang="scss">
.TagsElement {
	width: 100%;

	@include Breakpoint(Large, ExtraLarge) {
		max-width: 24.5rem;
	}

	.ChipsList {
		display: flex;
		flex-wrap: wrap;
		gap: 0.5rem;
		transition: max-height 0.3s ease;
		overflow: hidden;

		@include PaddingAreas(0, 0, 0.5, 0);

		@include Breakpoint(Large, ExtraLarge) {
			max-width: 24.5rem;
			max-height: 4.46rem;
		}

		&.expanded {
			max-height: var(--expanded-height, 15rem);
		}

		&.hasOverflow {
			border-bottom: 1px solid $Grey-200;
		}
	}

	.TagsActions {
		display: flex;
		align-items: center;
		margin-top: 0.5rem;
		justify-content: flex-end;

		.ShowMoreBtn {
			display: flex;
			align-items: center;
			background: none;
			border: none;
			color: $Blue-700;
			cursor: pointer;
			gap: 0.25rem;
			user-select: none;

			@include TextStyle(UiMediumSemibold);
		}

		.ShowMoreImg {
			min-width: 1rem;
			min-height: 1rem;

			@include TextStyle(UiLargeRegular);
		}
	}
}
</style>
